/**
 * Root Cause Analysis Tab (Step 2) for NCR Edit
 */

import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Save, Send, Lock, Unlock } from 'lucide-react';
import { NCREditFormData } from './types';

interface Step2TabProps {
  formData: NCREditFormData;
  canEdit: boolean;
  saving: boolean;
  // CR-5999: per-field validation errors for the RCA section, keyed by
  // API field name (why_1, selected_root_cause, immediate_corrective_action).
  // When a key is present the corresponding Textarea renders with a red
  // border and an error message beneath it.
  fieldErrors?: Record<string, string>;
  /**
   * Whether the current user is an authorised internal user (customer /
   * Helix admin) who may override the supplier-only lock and edit the RCA
   * section directly through the portal. When true a "Master Edit" toggle
   * is shown; enabling it unlocks the fields and the Save button for the
   * internal user. The supplier's normal `canEdit` flow is unaffected.
   */
  canMasterEdit?: boolean;
  /** Internal NCR — the 8D is owned by the customer's assigned team, not the
   *  supplier, so the section is labelled "Internal" rather than "Supplier". */
  isInternal?: boolean;
  onUpdateFormData: (field: string, value: unknown) => void;
  onSave: () => void;
  onSubmitRCA: () => void;
}

export function RootCauseTab({
  formData,
  canEdit,
  saving,
  fieldErrors = {},
  canMasterEdit = false,
  isInternal = false,
  onUpdateFormData,
  onSave,
  onSubmitRCA,
}: Step2TabProps) {
  // Master override (customer/admin). Off by default so an internal user
  // viewing the NCR sees the supplier's read-only RCA until they explicitly
  // take over editing — avoids accidental edits to supplier-owned content.
  const [masterEditing, setMasterEditing] = useState(false);
  // Effective edit gate: the supplier's normal workflow edit OR an internal
  // user who has switched Master Edit on.
  const editable = canEdit || (canMasterEdit && masterEditing);
  const errorTextareaClass =
    'border-red-500 focus-visible:ring-red-500 focus-visible:border-red-500';
  const renderError = (field: string) =>
    fieldErrors[field] ? (
      <p
        id={`${field}-error`}
        role="alert"
        className="text-sm text-red-600 mt-1"
      >
        {fieldErrors[field]}
      </p>
    ) : null;

  return (
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-3">
            <CardTitle>Root Cause Analysis ({isInternal ? 'Internal' : 'Supplier Only'})</CardTitle>
            {/* Master override for authorised internal users. The supplier
                owns this section; an admin/customer can take over editing
                when the supplier is unable to (e.g. on their behalf). */}
            {canMasterEdit && !canEdit && (
              <Button
                type="button"
                variant={masterEditing ? 'secondary' : 'outline'}
                size="sm"
                onClick={() => setMasterEditing((v) => !v)}
              >
                {masterEditing ? (
                  <>
                    <Unlock className="h-4 w-4 mr-2" />
                    Master Edit On
                  </>
                ) : (
                  <>
                    <Lock className="h-4 w-4 mr-2" />
                    Master Edit
                  </>
                )}
              </Button>
            )}
          </div>
          {canMasterEdit && masterEditing && !canEdit && (
            <p className="text-xs text-amber-600 mt-1">
              You are editing the supplier&rsquo;s Root Cause Analysis as an
              administrator. Changes are recorded against your account.
            </p>
          )}
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* CR-7069: 8D D1 Team Formation */}
            <div className="border-b pb-4 mb-4">
              <h4 className="text-sm font-semibold mb-3">8D D1: Team Formation</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Team Leader / Champion</Label>
                  <Input
                    value={(formData['team_leader'] as string) || ''}
                    onChange={(e) => onUpdateFormData('team_leader', e.target.value)}
                    disabled={!editable}
                    placeholder="Name of the 8D team leader"
                  />
                </div>
                <div>
                  <Label>Team Formation Date</Label>
                  <Input
                    type="date"
                    value={(formData['team_formation_date'] as string) || ''}
                    onChange={(e) => onUpdateFormData('team_formation_date', e.target.value || null)}
                    disabled={!editable}
                  />
                </div>
              </div>
              <div className="mt-3">
                <Label>Team Members</Label>
                <Textarea
                  value={(formData['team_members'] as string) || ''}
                  onChange={(e) => onUpdateFormData('team_members', e.target.value)}
                  disabled={!editable}
                  rows={2}
                  placeholder="Enter team member names (one per line)"
                />
              </div>
            </div>

            <div>
              <Label>Immediate Containment Action *</Label>
              <Textarea
                value={formData.immediate_corrective_action || ''}
                onChange={(e) => onUpdateFormData('immediate_corrective_action', e.target.value)}
                disabled={!editable}
                rows={3}
                aria-invalid={!!fieldErrors['immediate_corrective_action']}
                aria-describedby={fieldErrors['immediate_corrective_action'] ? 'immediate_corrective_action-error' : undefined}
                className={fieldErrors['immediate_corrective_action'] ? errorTextareaClass : ''}
              />
              {renderError('immediate_corrective_action')}
            </div>

            <div>
              <Label>Supplier Disposition Instructions</Label>
              <Textarea
                value={formData.disposition_instructions || ''}
                onChange={(e) => onUpdateFormData('disposition_instructions', e.target.value)}
                disabled={!editable}
                rows={2}
                maxLength={500}
              />
            </div>

            {/* CR-7069: Only Why 1 is required; Why 2-5 are optional so the supplier
                can stop at any depth when the root cause is identified. */}
            {[1, 2, 3, 4, 5].map((num) => {
              const field = `why_${num}`;
              return (
                <div key={num}>
                  <Label>Why {num}?{num === 1 && ' *'}</Label>
                  <Textarea
                    value={(formData[field] as string) || ''}
                    onChange={(e) => onUpdateFormData(field, e.target.value)}
                    disabled={!editable}
                    rows={2}
                    maxLength={500}
                    aria-invalid={!!fieldErrors[field]}
                    aria-describedby={fieldErrors[field] ? `${field}-error` : undefined}
                    className={fieldErrors[field] ? errorTextareaClass : ''}
                  />
                  {renderError(field)}
                </div>
              );
            })}

            <div>
              <Label>Selected Root Cause *</Label>
              <Textarea
                value={formData.selected_root_cause || ''}
                onChange={(e) => onUpdateFormData('selected_root_cause', e.target.value)}
                disabled={!editable}
                rows={3}
                maxLength={500}
                aria-invalid={!!fieldErrors['selected_root_cause']}
                aria-describedby={fieldErrors['selected_root_cause'] ? 'selected_root_cause-error' : undefined}
                className={fieldErrors['selected_root_cause'] ? errorTextareaClass : ''}
              />
              {renderError('selected_root_cause')}
            </div>

            {canEdit && (
              <div className="flex gap-2">
                <Button onClick={onSave} disabled={saving} variant="outline">
                  <Save className="h-4 w-4 mr-2" />
                  Save
                </Button>
                <Button onClick={onSubmitRCA} disabled={saving}>
                  <Send className="h-4 w-4 mr-2" />
                  Submit RCA
                </Button>
              </div>
            )}

            {/* Master editor (internal override): Save only. Submit RCA is a
                supplier workflow transition (created -> ca_submitted) so it is
                intentionally not offered here — the admin amends content
                without advancing the supplier's stage. */}
            {!canEdit && canMasterEdit && masterEditing && (
              <div className="flex gap-2">
                <Button onClick={onSave} disabled={saving} variant="outline">
                  <Save className="h-4 w-4 mr-2" />
                  Save
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
  );
}
